import { HandoffRuleEditorValue, VariableExtractionSchemaNode } from './contracts';

export type HandoffRuleIssueCode =
  | 'missingFromAgent'
  | 'missingToAgent'
  | 'duplicateDirection'
  | 'duplicateRoute'
  | 'invalidContextMaxMessages'
  | 'invalidExtractionRootType'
  | 'invalidExtractionAdditionalProperties'
  | 'invalidExtractionRequiredReference'
  | 'invalidExtractionPropertyName'
  | 'invalidExtractionArrayItems'
  | 'emptyMessageValue'
  | 'duplicateEndingAgents';

export type HandoffRuleIssue = {
  ruleId: string;
  code: HandoffRuleIssueCode;
};

export const validateHandoffRule = (rule: HandoffRuleEditorValue): HandoffRuleIssue[] => {
  const issues: HandoffRuleIssue[] = [];
  if (!rule.fromAgentId) issues.push({ ruleId: rule.id, code: 'missingFromAgent' });
  if (!rule.toAgentId) issues.push({ ruleId: rule.id, code: 'missingToAgent' });
  if (rule.fromAgentId && rule.toAgentId && rule.fromAgentId === rule.toAgentId) {
    issues.push({ ruleId: rule.id, code: 'duplicateDirection' });
  }
  if (rule.contextPropagationPlan?.type === 'lastNMessages') {
    const maxMessages = rule.contextPropagationPlan.maxMessages;
    if (!maxMessages || maxMessages < 1)
      issues.push({ ruleId: rule.id, code: 'invalidContextMaxMessages' });
  }
  const extractionSchema = rule.variableExtractionPlan?.schema;
  if (rule.variableExtractionPlan && (!extractionSchema || extractionSchema.type !== 'object')) {
    issues.push({ ruleId: rule.id, code: 'invalidExtractionRootType' });
  }
  if (extractionSchema && extractionSchema.additionalProperties !== false) {
    issues.push({ ruleId: rule.id, code: 'invalidExtractionAdditionalProperties' });
  }
  const validateNode = (node?: VariableExtractionSchemaNode) => {
    if (!node) return;
    if (node.type === 'object') {
      const keys = Object.keys(node.properties ?? {});
      if (keys.some((name) => !name.trim()))
        issues.push({ ruleId: rule.id, code: 'invalidExtractionPropertyName' });
      const required = node.required ?? [];
      if (required.some((name) => !keys.includes(name))) {
        issues.push({ ruleId: rule.id, code: 'invalidExtractionRequiredReference' });
      }
      Object.values(node.properties ?? {}).forEach((child) => validateNode(child));
    }
    if (node.type === 'array') {
      if (!node.items) {
        issues.push({ ruleId: rule.id, code: 'invalidExtractionArrayItems' });
      } else {
        validateNode(node.items);
      }
    }
  };
  validateNode(extractionSchema);
  const hasEmptyMessage = Object.values(rule.messages).some((group) =>
    group.some((message) => !message.trim()),
  );
  if (hasEmptyMessage) issues.push({ ruleId: rule.id, code: 'emptyMessageValue' });
  const endingAgents = rule.sessionBoundaryPlan?.endingAgentIds ?? [];
  if (new Set(endingAgents).size !== endingAgents.length) {
    issues.push({ ruleId: rule.id, code: 'duplicateEndingAgents' });
  }
  return issues.filter(
    (issue, index, list) => list.findIndex((item) => item.code === issue.code) === index,
  );
};

export const validateHandoffRules = (rules: HandoffRuleEditorValue[]): HandoffRuleIssue[] =>
  rules.flatMap((rule, index) => {
    const baseIssues = validateHandoffRule(rule);
    if (!rule.fromAgentId || !rule.toAgentId) return baseIssues;
    const hasDuplicateRoute = rules.some(
      (otherRule, otherIndex) =>
        otherIndex !== index &&
        otherRule.fromAgentId === rule.fromAgentId &&
        otherRule.toAgentId === rule.toAgentId,
    );
    return hasDuplicateRoute
      ? [...baseIssues, { ruleId: rule.id, code: 'duplicateRoute' }]
      : baseIssues;
  });
